import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { points } from "@volca/ui/trades";
import type { RecordObject } from "@/profitability/records/objects";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { reportError } from "@/profitability/utils";
import { ChartCard } from "./chart-card";
import { CostMix } from "./cost-mix";
import { periodLabel, periodOf } from "./format";
import {
  InsightsBreakdownNote,
  InsightsNothing,
  type InsightsActions,
  InsightsStart,
} from "./insights-empty";
import {
  chartSlots,
  InsightsLayoutSkeleton,
  InsightsSentenceSkeleton,
} from "./insights-skeleton";
import { MarginChart } from "./margin-chart";
import { moverOf } from "./margin-series";
import { useInsights } from "./query";
import { RulesTable } from "./rules-table";
import { SegmentDotPlot } from "./segment-dot-plot";
import { pickSeries } from "./series";
import type { TrendDataset } from "./types";

/**
 * What the costs are a share of, in a sentence: the label of the dataset's
 * `role: total` column, as the API names it for the latest period.
 */
const totalOf = (dataset: TrendDataset) =>
  dataset.total_label ? dataset.total_label.toLowerCase() : "the total";

/**
 * The template's periods side by side: what changed, where, and why. Until
 * there are two periods, or with nothing to follow across them, it says why
 * and offers the step that changes it (`insights-empty`).
 */
export const InsightsTab = ({
  businessId,
  reportId,
  objects = [],
  throughRunId = null,
  published = true,
  later = false,
  building = false,
  actions = {},
  reading = null,
  lead = null,
}: {
  businessId: string;
  reportId: string;
  /** The period's records, for what the work references. */
  objects?: RecordObject[];
  /** The period on screen, which the insights end with; null for the latest. */
  throughRunId?: string | null;
  /** False while the agent is still being set up: no period can run yet. */
  published?: boolean;
  /** The period on screen is not the latest: a later one already ran. */
  later?: boolean;
  /** The first period is still running: nothing to run until it finishes. */
  building?: boolean;
  /** What an empty Insights offers, for someone who may take it. */
  actions?: InsightsActions;
  /** What stands out this period. It follows the comparison and sits by the chart. */
  reading?: ReactNode;
  /** The period's figures, across the top. */
  lead?: ReactNode;
}) => {
  const insights = useInsights(businessId, reportId, throughRunId, {
    shape: true,
  });
  const spec = useReviewSpec();
  const metric = spec.metric.label;
  const work = objects.find((object) => object.dataset.id === spec.work);
  const referenced = (work?.dataset.columns ?? []).flatMap((column) =>
    column.references ? [column.references] : [],
  );
  // A cached period says which charts this report draws. Its numbers stay
  // off the page: the new week fills the same slots.
  const slots = insights.data
    ? chartSlots(insights.data, referenced)
    : undefined;
  const frame = (child: ReactNode) =>
    lead || reading ? (
      <Stack gap={8} align="stretch" className="@container w-full">
        {lead}
        {reading}
        {child}
      </Stack>
    ) : (
      child
    );
  if (insights.isError)
    return frame(
      <ErrorState
        title="Insights could not load"
        description={reportError(insights.error)}
        retrying={insights.isFetching}
      >
        <Button onClick={() => void insights.refetch()}>Try again</Button>
      </ErrorState>,
    );
  if (insights.isPending || insights.isPlaceholderData || !insights.data)
    return (
      <Stack
        gap={8}
        align="stretch"
        className="@container w-full"
        aria-busy
        role="status"
      >
        <span className="sr-only">Loading insights</span>
        {lead}
        {slots === null ? (
          <Stack gap={8} align="stretch">
            {reading}
            <InsightsSentenceSkeleton />
          </Stack>
        ) : (
          <InsightsLayoutSkeleton reading={reading} charts={slots} />
        )}
      </Stack>
    );
  const { periods, datasets, rules, flagged, reason_code } = insights.data;
  const noun = periodOf(periods);
  if (periods.length < 2)
    return frame(
      <InsightsStart
        noun={noun}
        periods={periods.length}
        published={published}
        later={later}
        building={building}
        actions={actions}
      />,
    );
  const gap =
    reason_code === "no_rollups" || reason_code === "untracked_rollups"
      ? reason_code
      : null;
  if (gap && rules.length === 0)
    return frame(
      <InsightsNothing reason={gap} noun={noun} actions={actions} />,
    );
  const { over: units, ranked: people } = pickSeries(datasets, referenced);
  const total = units ? totalOf(units) : "the total";
  const mover = units && moverOf(periods, units);
  // The last period run by another calculation: changes are measured only
  // after it, and the page says so quietly.
  const changed = periods.findLast((period) => !period.comparable);
  const chartNote = mover
    ? `${mover.key} moved most, ${points(mover.change)} since ${periodLabel(mover.since.start_date, noun)}`
    : changed
      ? `No change is measured across the calculation change`
      : null;
  // Figures, then the lines beside the one chart they are about. The rest
  // of the series sits under that, the way a reading leads and the detail follows.
  return (
    <Stack gap={8} align="stretch" className="@container w-full">
      {lead}
      {gap && (
        <InsightsBreakdownNote reason={gap} noun={noun} actions={actions} />
      )}
      {changed && (
        <Text variant="caption" tone="muted">
          The calculation changed after {periodLabel(changed.start_date, noun)};
          earlier {noun}s are shown as they were run.
        </Text>
      )}
      {(reading || units) && (
        <div
          className={cn("grid w-full min-w-0 items-start gap-8", {
            "@3xl:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]":
              !!reading && !!units,
          })}
        >
          {reading}
          {units && (
            <ChartCard
              className="min-w-0"
              title={`${metric} by ${noun}`}
              description={chartNote ?? undefined}
            >
              <MarginChart periods={periods} dataset={units} noun={noun} />
            </ChartCard>
          )}
        </div>
      )}
      {(people || (units && units.costs.length > 0) || rules.length > 0) && (
        <div className="grid w-full min-w-0 grid-cols-1 gap-6 @3xl:grid-cols-2">
          {people && (
            <ChartCard
              title={`${people.plural}: ${metric} this ${noun}`}
              description={`Lowest first, against the ${noun} before`}
            >
              <SegmentDotPlot periods={periods} dataset={people} noun={noun} />
            </ChartCard>
          )}
          {units && units.costs.length > 0 && (
            <ChartCard
              title={`Where ${total} went`}
              description={`Each cost as a share of ${total} this ${noun}`}
            >
              <CostMix
                periods={periods}
                dataset={units}
                noun={noun}
                total={total}
              />
            </ChartCard>
          )}
          {rules.length > 0 && (
            <div className="@3xl:col-span-2">
              <RulesTable
                reportId={reportId}
                runId={throughRunId}
                rules={rules}
                periods={periods}
                noun={noun}
                flagged={(flagged?.plural ?? "records").toLowerCase()}
              />
            </div>
          )}
        </div>
      )}
    </Stack>
  );
};
